import React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { ToxnotServiceClient } from "../../store/ToxnotServiceClient";
import { CreateSurveyCampaignRequest, SurveyCampaignTableEntry, UpdateSurveyCampaignRequest } from "../../store/Models/Api/Surveys/SurveyCampaignModels";
import SurveyTemplateManagerBundleResponse from "../../store/Models/Api/SurveyTemplateManagerBundleResponse";
import { TeamRole } from "../../store/Models/UserContext";
import { ToxnotComponentProperties } from "../core/BaseApplicationComponents/ToxnotComponent";
import { ToxnotSignInRequiredComponent } from "../core/BaseApplicationComponents/ToxnotSignInRequiredComponent";
import Layout from "../Layout";
import { LoadingSpinner, LoadingSpinnerSize } from "../core/LoadingSpinner";
import { TableColumn, TableRow, ToxnotReactTable } from "../core/ToxnotReactTable";
import { ScrollableContent, TopActionButtons, TopActionsAndScrollableContentContainer } from "../core/TopActionsAndScrollableContentContainer";

type SurveyCampaignsProps = ToxnotComponentProperties;
type SurveyCampaignSortColumnName = "campaignName" | "description" | "templateName" | "totalSurveyCount" | "requestInitiated" | "sent" | "inProgress" | "escalated" | "responseReceived" | "responseAccepted";

interface LocalState {
    campaigns: SurveyCampaignTableEntry[];
    pageIndex: number;
    totalNumCampaigns: number;
    surveyTemplateBundle: SurveyTemplateManagerBundleResponse | null;
    isLoadingCampaigns: boolean;
    loadCampaignsFailed: boolean;
    isTemplateBundleLoading: boolean;
    templateBundleLoadFailed: boolean;
    editingCampaignId: string | null;
    campaignName: string;
    description: string;
    templateId: string;
    validationMessage: string | null;
    saveErrorMessage: string | null;
    successMessage: string | null;
    isSavingCampaign: boolean;
}

class SurveyCampaigns extends ToxnotSignInRequiredComponent<SurveyCampaignsProps, LocalState> {
    private readonly _serviceClient: ToxnotServiceClient;
    private readonly _defaultSortColumn: SurveyCampaignSortColumnName = "campaignName";
    private readonly _campaignSortColumnNames: SurveyCampaignSortColumnName[] = [
        "campaignName",
        "description",
        "templateName",
        "totalSurveyCount",
        "requestInitiated",
        "sent",
        "inProgress",
        "escalated",
        "responseReceived",
        "responseAccepted"
    ];
    private readonly _rowsPerPage = 50;
    private _sortColumnName: SurveyCampaignSortColumnName;
    private _sortAscending: boolean;
    private _successMessageTimeoutId: number | null;

    public constructor(props: SurveyCampaignsProps) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();
        this._sortColumnName = this._defaultSortColumn;
        this._sortAscending = true;
        this._successMessageTimeoutId = null;

        this.state = {
            campaigns: [],
            pageIndex: 0,
            totalNumCampaigns: 0,
            surveyTemplateBundle: null,
            isLoadingCampaigns: true,
            loadCampaignsFailed: false,
            isTemplateBundleLoading: false,
            templateBundleLoadFailed: false,
            editingCampaignId: null,
            campaignName: "",
            description: "",
            templateId: "",
            validationMessage: null,
            saveErrorMessage: null,
            successMessage: null,
            isSavingCampaign: false
        };
    }

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    public async componentDidMount() {
        await this.loadCampaigns();
    }

    public componentWillUnmount() {
        if (this._successMessageTimeoutId !== null) {
            window.clearTimeout(this._successMessageTimeoutId);
            this._successMessageTimeoutId = null;
        }
    }

    protected renderConcreteComponent(): JSX.Element | null {
        if (this.state.isLoadingCampaigns) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: true }}>
                    <div className="tn-section">
                        <div className="text-center">
                            <LoadingSpinner size={LoadingSpinnerSize.Large} />
                        </div>
                    </div>
                </Layout>
            );
        }

        if (this.state.loadCampaignsFailed) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: true }}>
                    <div className="tn-section">
                        <p>{this.localizeString("Sorry, there was a problem accessing the service. Please try again.")}</p>
                    </div>
                </Layout>
            );
        }

        return (
            <Layout options={{ pinAppContentToWindowHeight: true }} modalsRender={this.renderModals}>
                <TopActionsAndScrollableContentContainer customClassName="survey-campaigns-root" shouldMakeTnSection={true} tnSectionIsRaw={false}>
                    <div className="tn-section-row tn-section-heading-row">
                        <div className="tn-section-col-12">
                            <h1>{this.localizeString("My Campaigns")}</h1>
                        </div>
                    </div>
                    {this.state.successMessage !== null && (
                        <div className="tn-section-row">
                            <div className="tn-section-col-12">
                                <div className="alert alert-success" role="alert">
                                    {this.state.successMessage}
                                </div>
                            </div>
                        </div>
                    )}
                    {this.getUserContextForSignedInUser().isEditor && (
                        <TopActionButtons formatButtons={true}>
                            <button type="button" className="btn btn-toxnot-primary" onClick={this.addCampaignButton_click}>
                                <span className="glyphicon glyphicon-plus"></span> {this.localizeString("Add Campaign")}
                            </button>
                        </TopActionButtons>
                    )}
                    <ScrollableContent>
                        <ToxnotReactTable<SurveyCampaignTableEntry>
                            headerRow={this.getCampaignTableHeaderRow()}
                            allRowModels={this.state.campaigns}
                            getRowForTable={this.getCampaignTableRow}
                            rowsPerPage={this._rowsPerPage}
                            localizer={this.props.appServices}
                            stickyHeader={true}
                            enablePaging={true}
                            fullSectionWidth={true}
                            currentPage={this.state.pageIndex}
                            defaultSortColumn={0}
                            totalNoResult={this.state.totalNumCampaigns}
                            onPageChange={this.table_onPageChange}
                            onSortChange={this.table_onSortChange}
                            noResultFoundContent={this.renderEmptyState()}
                            disableClientSorting={true}
                            usePublicationsSortBehavior={true}
                        />
                    </ScrollableContent>
                </TopActionsAndScrollableContentContainer>
            </Layout>
        );
    }

    private loadCampaigns = async (
        pageIndex: number = this.state.pageIndex,
        sortColumnName: SurveyCampaignSortColumnName = this._sortColumnName,
        sortAscending: boolean = this._sortAscending) => {
        this._sortColumnName = sortColumnName;
        this._sortAscending = sortAscending;

        const apiResult = await this._serviceClient.surveyCampaignsGetAll(
            this._rowsPerPage,
            pageIndex,
            sortColumnName,
            sortAscending);

        if (!apiResult.success || apiResult.data === null) {
            this.setState({
                isLoadingCampaigns: false,
                loadCampaignsFailed: true
            });

            return false;
        }

        this.setState({
            campaigns: apiResult.data.pageOfCampaigns,
            pageIndex: pageIndex,
            totalNumCampaigns: apiResult.data.totalNumCampaigns,
            isLoadingCampaigns: false,
            loadCampaignsFailed: false
        });

        return true;
    }

    private loadSurveyTemplateBundle = async (selectedTemplateId: string = "", useDefaultTemplateIfEmpty: boolean = true) => {
        this.setState({
            surveyTemplateBundle: null,
            isTemplateBundleLoading: true,
            templateBundleLoadFailed: false,
            templateId: selectedTemplateId,
            validationMessage: null,
            saveErrorMessage: null
        });

        const apiResult = await this._serviceClient.surveyTemplateManagerBundleGet();

        if (!apiResult.success || apiResult.data === null) {
            this.setState({
                surveyTemplateBundle: null,
                isTemplateBundleLoading: false,
                templateBundleLoadFailed: true
            });

            return;
        }

        const normalizedSelectedTemplateId =
            selectedTemplateId.length > 0 && apiResult.data.templates.some(template => template.templateId === selectedTemplateId)
                ? selectedTemplateId
                : "";

        this.setState({
            surveyTemplateBundle: apiResult.data,
            isTemplateBundleLoading: false,
            templateBundleLoadFailed: false,
            templateId: normalizedSelectedTemplateId || (useDefaultTemplateIfEmpty ? (apiResult.data.defaultSurveyTemplateId || "") : "")
        });
    }

    private getCampaignTableHeaderRow = (): TableColumn<SurveyCampaignTableEntry>[] => {
        const columns: TableColumn<SurveyCampaignTableEntry>[] = [
            {
                value: this.localizeString("Campaign Name"),
                isSortable: true,
                getSortableValue: campaign => campaign.campaignName.toLowerCase()
            },
            {
                value: this.localizeString("Description"),
                isSortable: true,
                getSortableValue: campaign => (campaign.description ?? "").toLowerCase()
            },
            {
                value: this.localizeString("Template"),
                isSortable: true,
                getSortableValue: campaign => (campaign.templateName ?? "").toLowerCase()
            },
            {
                value: this.localizeString("Total Survey"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.totalSurveyCount)
            },
            {
                value: this.localizeString("Request Initiated"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.requestInitiated)
            },
            {
                value: this.localizeString("Sent"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.sent)
            },
            {
                value: this.localizeString("In Progress"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.inProgress)
            },
            {
                value: this.localizeString("Escalated"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.escalated)
            },
            {
                value: this.localizeString("Response Received"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.responseReceived)
            },
            {
                value: this.localizeString("Response Accepted"),
                isSortable: true,
                getSortableValue: campaign => this.getSortableCountValue(campaign.responseAccepted)
            }
        ];

        columns.push({
            value: this.localizeString("Actions")
        });

        return columns;
    }

    private getCampaignTableRow = (campaign: SurveyCampaignTableEntry): TableRow => {
        const cols: TableRow["cols"] = [
            campaign.campaignName,
            campaign.description ?? "",
            campaign.templateName ?? "",
            campaign.totalSurveyCount.toString(),
            campaign.requestInitiated.toString(),
            campaign.sent.toString(),
            campaign.inProgress.toString(),
            campaign.escalated.toString(),
            campaign.responseReceived.toString(),
            campaign.responseAccepted.toString()
        ];

        const actionButtons: JSX.Element[] = [
            <a
                key="view"
                href={this.getViewSurveysHref(campaign)}
                className="btn btn-default btn-sm"
                title={this.localizeString("View Surveys")}
                aria-label={this.localizeString("View Surveys")}
            >
                <img src="/images/nav/surveys-icon-3.svg" height="14" aria-hidden="true" alt="" />
            </a>
        ];

        if (this.getUserContextForSignedInUser().isEditor) {
            const isEditDisabled = campaign.totalSurveyCount > 0;

            actionButtons.push(
                <button
                    key="edit"
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => this.editCampaignButton_click(campaign)}
                    disabled={isEditDisabled}
                    title={isEditDisabled
                        ? this.localizeString("This campaign cannot be edited after a survey has been assigned.")
                        : this.localizeString("Edit Campaign")}
                    aria-label={this.localizeString("Edit Campaign")}
                >
                    <span className="glyphicon glyphicon-pencil"></span>
                </button>
            );
        }

        cols.push(
            <div className="btn-group btn-group-sm text-nowrap" role="group">
                {actionButtons}
            </div>
        );

        return {
            cols: cols
        };
    }

    private getSortableCountValue = (count: number): string => (`000000000000${count.toString()}`).slice(-12);

    private table_onPageChange = (pageNumber: number) => {
        void this.loadCampaigns(pageNumber);
    }

    private table_onSortChange = (columnIndex: number, sortAscending: boolean) => {
        const sortColumnName = this._campaignSortColumnNames[columnIndex];

        if (!sortColumnName) {
            return;
        }

        void this.loadCampaigns(0, sortColumnName, sortAscending);
    }

    private getViewSurveysHref = (campaign: SurveyCampaignTableEntry) => (
        `/Surveys/SupplierSurveys?campaignId=${encodeURIComponent(campaign.campaignId)}`
    )

    private renderEmptyState = () => {
        const userContext = this.getUserContextForSignedInUser();

        return (
            <div className="text-center">
                <p>{this.localizeString("There are no campaigns defined.")}</p>
                {userContext.isEditor && this.state.campaigns.length > 0 && (
                    <p>
                        <button type="button" className="btn btn-toxnot-primary" onClick={this.addCampaignButton_click}>
                            <span className="glyphicon glyphicon-plus"></span> {this.localizeString("Add Campaign")}
                        </button>
                    </p>
                )}
            </div>
        );
    }

    private renderModals = () => {
        const isEditingCampaign = this.state.editingCampaignId !== null;
        const selectOptions = this.getTemplateOptions();
        const isTemplateSelectDisabled = this.state.isTemplateBundleLoading || this.state.isSavingCampaign || selectOptions.length === 0;

        return (
            <div id="survey-campaign-create-modal" className="modal fade" tabIndex={-1} role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <form onSubmit={this.campaignForm_submit}>
                            <div className="modal-header">
                                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                                <h4 className="modal-title">{isEditingCampaign ? this.localizeString("Edit Campaign") : this.localizeString("New Campaign")}</h4>
                            </div>
                            <div className="modal-body">
                                {this.state.isTemplateBundleLoading && (
                                    <div className="alert alert-info" role="alert">
                                        {this.localizeString("Loading...")}
                                    </div>
                                )}
                                {this.state.templateBundleLoadFailed && (
                                    <div className="alert alert-danger" role="alert">
                                        {this.localizeString("Sorry, there was a problem accessing the service. Please try again.")}
                                    </div>
                                )}
                                {this.state.validationMessage !== null && (
                                    <div className="alert alert-danger" role="alert">
                                        {this.state.validationMessage}
                                    </div>
                                )}
                                {this.state.saveErrorMessage !== null && (
                                    <div className="alert alert-danger" role="alert">
                                        {this.state.saveErrorMessage}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label htmlFor="survey-campaign-name-input">
                                        {this.localizeString("Name")} <span className="required-marker">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="survey-campaign-name-input"
                                        className="form-control"
                                        maxLength={250}
                                        value={this.state.campaignName}
                                        onChange={this.campaignNameInput_change}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="survey-campaign-description-input">
                                        {this.localizeString("Description")} <span className="required-marker">*</span>
                                    </label>
                                    <textarea
                                        id="survey-campaign-description-input"
                                        className="form-control"
                                        rows={4}
                                        maxLength={4000}
                                        value={this.state.description}
                                        onChange={this.descriptionInput_change}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="survey-campaign-template-select">
                                        {this.localizeString("Survey Template")} <span className="required-marker">*</span>
                                    </label>
                                    <select
                                        id="survey-campaign-template-select"
                                        className="form-control"
                                        disabled={isTemplateSelectDisabled}
                                        value={this.state.templateId}
                                        onChange={this.templateSelect_change}
                                    >
                                        <option value="">
                                            {this.state.isTemplateBundleLoading
                                                ? this.localizeString("Loading...")
                                                : this.state.templateBundleLoadFailed
                                                    ? this.localizeString("Unable to load survey templates")
                                                    : this.localizeString("Select a Survey Template")}
                                        </option>
                                        {selectOptions.map(template => (
                                            <option key={template.templateId ?? template.name} value={template.templateId ?? ""}>
                                                {template.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" data-dismiss="modal">
                                    {this.localizeString("Cancel")}
                                </button>
                                <button type="submit" className="btn btn-toxnot-primary" disabled={this.state.isSavingCampaign}>
                                    {isEditingCampaign ? this.localizeString("Save") : this.localizeString("Create Campaign")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    private getTemplateOptions() {
        return (this.state.surveyTemplateBundle?.templates ?? [])
            .filter(template => template.templateId !== null)
            .slice()
            .sort((left, right) => left.name.localeCompare(right.name));
    }

    private addCampaignButton_click = async () => {
        await this.openCampaignModal();
    }

    private editCampaignButton_click = async (campaign: SurveyCampaignTableEntry) => {
        await this.openCampaignModal(campaign);
    }

    private openCampaignModal = async (campaign: SurveyCampaignTableEntry | null = null) => {
        this.resetCampaignForm(campaign);
        this.showModal("survey-campaign-create-modal");
        this.focusCampaignNameInput();
        await this.loadSurveyTemplateBundle(campaign?.templateId ?? "", campaign === null);
    }

    private focusCampaignNameInput = () => {
        window.setTimeout(() => {
            const input = document.getElementById("survey-campaign-name-input") as HTMLInputElement | null;

            input?.focus();
        }, 250);
    };

    private resetCampaignForm = (campaign: SurveyCampaignTableEntry | null = null) => {
        this.setState({
            surveyTemplateBundle: null,
            isTemplateBundleLoading: false,
            templateBundleLoadFailed: false,
            editingCampaignId: campaign?.campaignId ?? null,
            campaignName: campaign?.campaignName ?? "",
            description: campaign?.description ?? "",
            templateId: campaign?.templateId ?? "",
            validationMessage: null,
            saveErrorMessage: null,
            isSavingCampaign: false
        });
    }

    private campaignForm_submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const isEditingCampaign = this.state.editingCampaignId !== null;
        const campaignName = this.state.campaignName.trim();
        const description = this.state.description.trim();

        if (campaignName.length === 0) {
            this.setState({
                validationMessage: this.localizeString("Please enter a campaign name."),
                saveErrorMessage: null
            });

            return;
        }

        if (description.length === 0) {
            this.setState({
                validationMessage: this.localizeString("Please enter a description."),
                saveErrorMessage: null
            });

            return;
        }

        if (this.state.templateId.length === 0) {
            this.setState({
                validationMessage: this.localizeString("Please select a survey template."),
                saveErrorMessage: null
            });

            return;
        }

        this.setState({
            validationMessage: null,
            saveErrorMessage: null,
            isSavingCampaign: true
        });

        const apiResult = isEditingCampaign
            ? await this._serviceClient.surveyCampaignUpdate({
                campaignId: this.state.editingCampaignId,
                campaignName: campaignName,
                description: description,
                templateId: this.state.templateId
            } as UpdateSurveyCampaignRequest)
            : await this._serviceClient.surveyCampaignCreate({
                campaignName: campaignName,
                description: description,
                templateId: this.state.templateId
            } as CreateSurveyCampaignRequest);

        if (!apiResult.success || apiResult.data === null) {
            this.setState({
                isSavingCampaign: false,
                saveErrorMessage: apiResult.resultMessage ?? this.localizeString("Sorry, there was a problem accessing the service. Please try again.")
            });

            return;
        }

        this.hideModal("survey-campaign-create-modal");

        const loadSucceeded = await this.loadCampaigns();

        if (!loadSucceeded) {
            this.setState({
                isSavingCampaign: false
            });

            return;
        }

        this.setState({
            isSavingCampaign: false,
            successMessage: isEditingCampaign
                ? this.localizeString("Campaign updated successfully.")
                : this.localizeString("Campaign saved successfully.")
        });

        this.startSuccessMessageTimeout();
    }

    private startSuccessMessageTimeout = () => {
        if (this._successMessageTimeoutId !== null) {
            window.clearTimeout(this._successMessageTimeoutId);
        }

        this._successMessageTimeoutId = window.setTimeout(() => {
            this.setState({
                successMessage: null
            });

            this._successMessageTimeoutId = null;
        }, 5000);
    }

    private campaignNameInput_change = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            campaignName: e.target.value
        });
    }

    private descriptionInput_change = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            description: e.target.value
        });
    }

    private templateSelect_change = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({
            templateId: e.target.value
        });
    }
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(SurveyCampaigns as any);
